import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getUserPhoto } from "../services/user.service";
import { Buffer } from "buffer";

const SELECTED_ROLE_STORAGE_KEY = "selectedUserRole";

const loadStoredSelectedRole = () => {
  if (typeof window === "undefined") {
    return null;
  }

  const storedRole = window.localStorage.getItem(SELECTED_ROLE_STORAGE_KEY);

  if (!storedRole) {
    return null;
  }

  try {
    return JSON.parse(storedRole);
  } catch {
    window.localStorage.removeItem(SELECTED_ROLE_STORAGE_KEY);
    return null;
  }
};

const persistSelectedRole = (role) => {
  if (typeof window === "undefined") {
    return;
  }

  if (!role) {
    window.localStorage.removeItem(SELECTED_ROLE_STORAGE_KEY);
    return;
  }

  window.localStorage.setItem(SELECTED_ROLE_STORAGE_KEY, JSON.stringify(role));
};

const getRoleName = (role) =>
  typeof role === "object" ? role?.roleName || role?.name || "" : role || "";

const isAdminRole = (role) =>
  getRoleName(role).trim().toLowerCase() === "admin";

const isRoleActive = (role) => {
  if (typeof role !== "object" || role === null) {
    return true;
  }

  if (typeof role.isActive === "boolean") {
    return role.isActive;
  }

  if (typeof role.status === "boolean") {
    return role.status;
  }

  if (typeof role.status === "string") {
    return role.status.trim().toLowerCase() !== "inactive";
  }

  return true;
};

const getRoleIdentity = (role) => {
  if (typeof role !== "object" || role === null) {
    return String(role || "");
  }

  return String(role.userGuid || role.roleId || getRoleName(role) || "");
};

const getActiveRoles = (roles) =>
  Array.isArray(roles)
    ? roles.filter((role) => !isAdminRole(role) && isRoleActive(role))
    : [];

const getDefaultRole = (roles) => {
  const activeRoles = getActiveRoles(roles);

  if (activeRoles.length === 0) {
    return null;
  }

  const didRole = activeRoles.find(
    (role) => getRoleName(role).trim().toLowerCase() === "did",
  );

  return didRole || activeRoles[0];
};

const resolveSelectedRole = (roles, preferredRole) => {
  const activeRoles = getActiveRoles(roles);

  if (activeRoles.length === 0) {
    return null;
  }

  if (preferredRole) {
    const preferredRoleIdentity = getRoleIdentity(preferredRole);
    const matchingRole = activeRoles.find(
      (role) => getRoleIdentity(role) === preferredRoleIdentity,
    );

    if (matchingRole) {
      return matchingRole;
    }
  }

  return getDefaultRole(activeRoles);
};

const initialState = {
  user: {},
  photo: "",
  isAuthenticated: false,
  jwtToken: "",
  userRoles: [],
  rolesLoaded: false, // Tracks if roles have been fetched from API
  selectedRole: null,
  userGuid: "",
  allowedOrderIds: [],
};

export const getPhoto = createAsyncThunk("me/photo", async (request) => {
  const apiResponse = await getUserPhoto({ request });

  let response = `data:image/jpeg;base64,${Buffer.from(
    apiResponse.data,
    "binary",
  ).toString("base64")}`;

  return response;
});

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = true;
    },
    setJWtToken: (state, action) => {
      state.jwtToken = action.payload;
    },
    setUserRoles: (state, action) => {
      state.userRoles = action.payload;
      state.rolesLoaded = true; // Mark roles as loaded once API returns
      const roleToSelect = resolveSelectedRole(
        action.payload,
        state.selectedRole || loadStoredSelectedRole(),
      );

      state.selectedRole = roleToSelect;
      state.userGuid = roleToSelect?.userGuid || "";
      persistSelectedRole(roleToSelect);
    },
    setSelectedRole: (state, action) => {
      const roleToSelect = resolveSelectedRole(state.userRoles, action.payload);

      state.selectedRole = roleToSelect;
      state.userGuid = roleToSelect?.userGuid || "";
      persistSelectedRole(roleToSelect);
    },
    setUserGuid: (state, action) => {
      state.userGuid = action.payload;
    },
    setAllowedOrderIds: (state, action) => {
      state.allowedOrderIds = action.payload;
    },
    logoutUser: (state) => {
      state.isAuthenticated = initialState.isAuthenticated;
      sessionStorage.clear();
      persistSelectedRole(null);
      state.user = initialState.user;
      state.userRoles = initialState.userRoles;
      state.rolesLoaded = initialState.rolesLoaded;
      state.selectedRole = null;
      state.userGuid = initialState.userGuid;
      state.allowedOrderIds = initialState.allowedOrderIds;
    },
  },
  extraReducers(builder) {
    builder.addCase(getPhoto.fulfilled, (state, action) => {
      state.photo = action.payload;
    });
  },
});

export const {
  setUser,
  logoutUser,
  setJWtToken,
  setUserRoles,
  setSelectedRole,
  setUserGuid,
  setAllowedOrderIds,
} = userSlice.actions;

export default userSlice.reducer;
